import { call, put, fork, takeLatest, takeEvery, all } from 'redux-saga/effects';
import objectAssignDeep from "deps/object-assign-deep";
import { mkPaginatedHandler } from 'api/paginatedHandler';
import * as api from './requests';

const watchers = [];

/**
 * Builds a handler for an API request, given a function and base
 * action name. This is a top-level internal API for managing
 * our API requests and getting their results to record in our
 * redux store and then pass it down to the components through
 * our state tree (handled in src/state/store.js).
 *
 * It can be initiated on the _REQUESTED call.
 *
 * Adds action handlers for the following states:
 * _PENDING, _SUCCESSFUL, _FAILED
 *
 * (In the above notation, base is prefixed to the action type.)
 *
 * The _SUCCESSFUL response returns the action:
 *
 * { type: _SUCCESSFUL, data: { API response body } }
 *
 * The _FAILED response emits the action:
 *
 * { type: _FAILED, error: { API error } }
 *
 * The _PENDING action doesn't have a payload and is
 * simply meant to drive a loading message, etc.
 */
const mkHandler = (fn, base, latest=true) => {
  function* handler(action) {
    // dispatch a pending action for loading indication
    yield put({type: `${base}_PENDING`});
    try {
      // don't directly invoke the function here, pass the instruction to
      // middleware so we can test this in isolation
      const data = yield call(fn, action.payload);
      yield put({type: `${base}_SUCCESSFUL`, payload: data });
    } catch (error) {
      const errorData = error.response ? error.response.data : {};
      // use objectAssignDeep here because we want a two-way merge (meaning
      // if the second object tries to assign {} for a key and the first
      // one has this: {a: 1}, the result will be {a: 1} instead of {}
      // as Object.assign would do it.
      const errorPayload = objectAssignDeep({}, errorData, {
        statusCode: error.response ? error.response.status : null,
      });
      yield put({
        type: `${base}_FAILED`,
        payload: errorPayload,
      });
    }
  }

  // cancel any pending requests if concurrent requested
  // in the future, if we need to handle concurrent multiple
  // requests on the same resource we can toggle this and
  // use watchEvery instead
  function* watchHandler() {
    let takeFn = takeLatest;
    if (!latest) {
      takeFn = takeEvery;
    }
    yield takeFn(`${base}_REQUESTED`, handler);
  }
  // push the watcher to the stack for fork initialization
  watchers.push(fork(watchHandler));
  // return the handler for testing
  return handler;
};

/**
 * Saga for handling login and subsequent user request
 */
function* loginSagaHandler(action) {
  yield put({type: "LOGIN_PENDING"});
  try {
    const loginData = yield call(api.login, action.payload);
    const authData = {
      username: action.payload.username,
      token: loginData.data.token,
    };
    const userData = yield call(api.getUser, authData);

    const data = {
      user: userData,
      auth: loginData,
    };
    yield put({type: "LOGIN_SUCCESSFUL", payload: data});
  } catch (error) {
    yield put({
      type: "LOGIN_FAILED",
      payload: {
        message: error.message
      }
    });
  }
}

function* watchLogin() {
  yield takeLatest("LOGIN_REQUESTED", loginSagaHandler);
}

/**
 * Saga bulk getting of vote prefs
 *
 * Action: "BULK_VOTE_PREFS_REQUESTED"
 * Payload:
 * {
 *   pageIds: ["sf-prop-a", "candidate-x", ...],
 * }
 */
function* getVotePrefsHandler(action) {
  const pageIds = action.payload.pageIds;
  const userId = action.payload.userId;
  try {
    const tasks = pageIds.map((pageId) => {
      return call(api.getVotePref, { pageId, userId });
    });
    const results = yield all(tasks);
    yield put({type: "BULK_VOTE_PREFS_SUCCESSFUL", payload: results});
  } catch (error) {
    yield put({
      type: "BULK_VOTE_PREFS_FAILED",
      payload: {
        message: error.message
      }
    });
  }
}

function* watchGetVotePrefs() {
  yield takeLatest("GET_VOTE_PREFS_REQUESTED", getVotePrefsHandler);
}

export const register = mkHandler(api.register, "REGISTER");
export const registerComplete = mkHandler(api.registerComplete, "REGISTER_COMPLETE");
export const getUser = mkHandler(api.getUser, "GET_USER");
export const updateUser = mkHandler(api.updateUser, "UPDATE_USER");
export const resetPassword = mkHandler(api.resetPassword, "RESET_PASSWORD");
export const resetPasswordComplete = mkHandler(
  api.resetPasswordComplete, "RESET_PASSWORD_COMPLETE");
export const subscriber = mkHandler(api.subscriber, "SUBSCRIBE");
export const donation = mkHandler(api.donation, "DONATION");
export const writeVotePref = mkHandler(api.writeVotePref, "WRITE_VOTE_PREF");
export const getVotePref = mkHandler(api.getVotePref, "GET_VOTE_PREF");
export const writeBallot = mkHandler(api.writeBallot, "WRITE_BALLOT");
export const getBallot = mkHandler(api.getBallot, "GET_BALLOT");
export const listComms = mkHandler(api.listComments, "LIST_COMMENTS", false);
export const createComment = mkHandler(api.createComment, "CREATE_COMMENT");
export const deleteComment = mkHandler(api.deleteComment, "DELETE_COMMENT");
export const updateComment = mkHandler(api.updateComment, "UPDATE_COMMENT");
export const upvoteComment = mkHandler(api.upvoteComment, "UPVOTE_COMMENT");
export const search = mkHandler(api.search, "SEARCH");
export const flag = mkHandler(api.flag, "FLAG");
const listAllComms = mkPaginatedHandler(
  api.listComments, "LIST_ALL_COMMENTS", false);
export const listAllComments = listAllComms.handler;

// run our saga watchers
watchers.push(fork(watchLogin));
watchers.push(fork(watchGetVotePrefs));
watchers.push(fork(listAllComms.watcher));

export default function* root() {
  yield all(watchers)
}




// WEBPACK FOOTER //
// ./src/api/apiHandlers.js